/**
 * ExecutionHistoryPanel Component
 * Displays execution history for workflow or system task schedules
 */

import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import {
  ArrowLeft,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader2,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  RefreshCw,
  User,
  Users,
  Calendar,
} from 'lucide-react';
import { useScheduleExecutions as useWorkflowExecutions } from '../hooks/useWorkflowSchedules';
import { useScheduleExecutions as useSystemTaskExecutions } from '../hooks/useSystemTaskSchedules';
import type { ScheduleCategory, SystemTaskExecution } from '@/types/systemTaskSchedule';

// Define execution type for workflow (since it's not typed in API)
interface WorkflowExecution {
  id: string;
  schedule_id: string;
  status: string;
  started_at?: string;
  completed_at?: string;
  duration_ms?: number;
  total_users?: number;
  successful_users?: number;
  failed_users?: number;
  target_user_ids?: string[];
  created_at?: string;
}

type Execution = WorkflowExecution | SystemTaskExecution;

interface ExecutionHistoryPanelProps {
  scheduleId: string;
  scheduleName?: string;
  category: ScheduleCategory;
  onBack: () => void;
}

// Status badge component
function StatusBadge({ status }: { status: string }) {
  const variants: Record<string, { bg: string; icon: React.ReactNode }> = {
    pending: { bg: 'bg-gray-100 text-gray-700', icon: <Clock className="h-3 w-3" /> },
    running: { bg: 'bg-blue-100 text-blue-700', icon: <Loader2 className="h-3 w-3 animate-spin" /> },
    completed: { bg: 'bg-green-100 text-green-700', icon: <CheckCircle className="h-3 w-3" /> },
    failed: { bg: 'bg-red-100 text-red-700', icon: <XCircle className="h-3 w-3" /> },
    success: { bg: 'bg-green-100 text-green-700', icon: <CheckCircle className="h-3 w-3" /> },
    error: { bg: 'bg-red-100 text-red-700', icon: <XCircle className="h-3 w-3" /> },
  };

  const labels: Record<string, string> = {
    pending: '等待中',
    running: '执行中',
    completed: '已完成',
    failed: '失败',
    success: '成功',
    error: '错误',
  };

  const variant = variants[status] || { bg: 'bg-gray-100 text-gray-700', icon: <AlertCircle className="h-3 w-3" /> };
  const label = labels[status] || status;

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${variant.bg}`}>
      {variant.icon}
      {label}
    </span>
  );
}

// Format datetime
function formatDateTime(dateString?: string): string {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

// Format duration
function formatDuration(ms?: number): string {
  if (ms === undefined || ms === null) return '-';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  const minutes = Math.floor(ms / 60000);
  const seconds = Math.floor((ms % 60000) / 1000);
  return `${minutes}m ${seconds}s`;
}

// Execution item component
function ExecutionItem({
  execution,
  category,
}: {
  execution: Execution;
  category: ScheduleCategory;
}) {
  const [expanded, setExpanded] = useState(false);
  const [showUserList, setShowUserList] = useState(false);

  const isSystemTask = category === 'system_task';
  const systemExecution = execution as SystemTaskExecution;
  const workflowExecution = execution as WorkflowExecution;

  // Get success status
  const getSuccessStatus = () => {
    if (isSystemTask) {
      if (systemExecution.success === true) return 'success';
      if (systemExecution.success === false) return 'failed';
    }
    return execution.status;
  };

  const status = getSuccessStatus();

  return (
    <Card className="mb-2">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            {/* Header row */}
            <div className="flex items-center gap-3 mb-2">
              <StatusBadge status={status} />
              <span className="text-sm text-gray-500">
                {formatDuration(execution.duration_ms)}
              </span>
              {isSystemTask && systemExecution.triggered_by && (
                <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                  <User className="h-3 w-3" />
                  {systemExecution.triggered_by}
                </span>
              )}
            </div>

            {/* Time info */}
            <div className="flex items-center gap-4 text-sm text-gray-600 mb-2">
              <div className="flex items-center gap-1">
                <Calendar className="h-4 w-4 text-gray-400" />
                <span>开始: {formatDateTime(execution.started_at || execution.created_at)}</span>
              </div>
              {execution.completed_at && (
                <div className="flex items-center gap-1">
                  <Clock className="h-4 w-4 text-gray-400" />
                  <span>结束: {formatDateTime(execution.completed_at)}</span>
                </div>
              )}
            </div>

            {/* Workflow specific: user stats */}
            {!isSystemTask && workflowExecution.total_users !== undefined && (
              <div className="space-y-2">
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-gray-500">用户: {workflowExecution.total_users}</span>
                  {workflowExecution.successful_users !== undefined && (
                    <span className="text-green-600">成功: {workflowExecution.successful_users}</span>
                  )}
                  {workflowExecution.failed_users !== undefined && workflowExecution.failed_users > 0 && (
                    <span className="text-red-600">失败: {workflowExecution.failed_users}</span>
                  )}
                  {workflowExecution.target_user_ids && workflowExecution.target_user_ids.length > 0 && (
                    <button
                      onClick={() => setShowUserList(!showUserList)}
                      className="flex items-center gap-1 text-sm font-medium text-indigo-600 hover:text-indigo-700 transition-colors"
                    >
                      {showUserList ? (
                        <ChevronDown className="w-4 h-4" />
                      ) : (
                        <ChevronRight className="w-4 h-4" />
                      )}
                      <Users className="w-4 h-4" />
                      <span>查看用户列表</span>
                    </button>
                  )}
                </div>

                {/* Target Users List (Collapsible) */}
                {showUserList && workflowExecution.target_user_ids && workflowExecution.target_user_ids.length > 0 && (
                  <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">目标用户 ID 列表:</h4>
                    <div className="max-h-40 overflow-y-auto">
                      <div className="grid grid-cols-2 gap-2">
                        {workflowExecution.target_user_ids.map((userId, index) => (
                          <div
                            key={index}
                            className="px-2 py-1 bg-white border border-gray-200 rounded text-xs font-mono text-gray-700"
                          >
                            {userId}
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="mt-2 text-xs text-gray-500">
                      共 {workflowExecution.target_user_ids.length} 个用户
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* System task specific: error message */}
            {isSystemTask && systemExecution.error_message && (
              <div className="mt-2 p-2 bg-red-50 rounded text-sm text-red-600">
                {systemExecution.error_message}
              </div>
            )}

            {/* System task specific: result summary */}
            {isSystemTask && systemExecution.result_summary && Object.keys(systemExecution.result_summary).length > 0 && (
              <div className="mt-2 p-2 bg-gray-50 rounded">
                <div className="text-xs text-gray-500 mb-1">执行结果摘要:</div>
                <div className="text-sm text-gray-700">
                  {Object.entries(systemExecution.result_summary).map(([key, value]) => (
                    <div key={key} className="flex gap-2">
                      <span className="text-gray-500">{key}:</span>
                      <span>{typeof value === 'object' ? JSON.stringify(value) : String(value)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Expand button for details */}
          {isSystemTask && systemExecution.result_details && Object.keys(systemExecution.result_details).length > 0 && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="p-1 rounded hover:bg-gray-100 ml-2"
            >
              {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
          )}
        </div>

        {/* Expanded details */}
        {expanded && isSystemTask && systemExecution.result_details && (
          <div className="mt-3 pt-3 border-t">
            <div className="text-xs text-gray-500 mb-1">执行详情:</div>
            <pre className="text-xs bg-gray-50 p-2 rounded overflow-x-auto max-h-60">
              {JSON.stringify(systemExecution.result_details, null, 2)}
            </pre>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function ExecutionHistoryPanel({
  scheduleId,
  scheduleName,
  category,
  onBack,
}: ExecutionHistoryPanelProps) {
  const [page, setPage] = useState(0);
  const limit = 20;

  // Fetch executions based on category
  const workflowQuery = useWorkflowExecutions(
    category === 'workflow' ? scheduleId : '',
    { limit, offset: page * limit }
  );

  const systemTaskQuery = useSystemTaskExecutions(
    category === 'system_task' ? scheduleId : '',
    { limit, offset: page * limit }
  );

  // Select the appropriate query based on category
  const query = category === 'workflow' ? workflowQuery : systemTaskQuery;
  const { data, isLoading, error, refetch, isFetching } = query;

  const executions = (data?.executions || []) as Execution[];
  const total = data?.total || 0;
  const totalPages = Math.ceil(total / limit);

  // Handle refresh
  const handleRefresh = () => {
    refetch();
  };

  return (
    <div className="h-full overflow-y-auto p-6 bg-gray-50">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="secondary" onClick={onBack}>
              <ArrowLeft className="h-4 w-4 mr-1" />
              返回列表
            </Button>
            <div>
              <h2 className="text-xl font-semibold">执行历史</h2>
              {scheduleName && (
                <p className="text-sm text-gray-500">{scheduleName}</p>
              )}
            </div>
          </div>
          <Button
            variant="secondary"
            onClick={handleRefresh}
            disabled={isFetching}
          >
            <RefreshCw className={`h-4 w-4 mr-1 ${isFetching ? 'animate-spin' : ''}`} />
            刷新
          </Button>
        </div>

        {/* Loading state */}
        {isLoading && (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-2" />
              <p className="text-gray-500">加载中...</p>
            </div>
          </div>
        )}

        {/* Error state */}
        {error && (
          <div className="flex items-center justify-center h-64">
            <div className="text-center text-red-500">
              <XCircle className="h-8 w-8 mx-auto mb-2" />
              <p>加载失败</p>
              <p className="text-sm">{(error as Error).message}</p>
              <Button variant="secondary" className="mt-4" onClick={handleRefresh}>
                重试
              </Button>
            </div>
          </div>
        )}

        {/* Empty state */}
        {!isLoading && !error && executions.length === 0 && (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Clock className="h-12 w-12 text-gray-300 mb-4" />
              <h3 className="text-lg font-medium mb-1">暂无执行记录</h3>
              <p className="text-sm text-gray-500">
                此定时任务尚未执行过
              </p>
            </CardContent>
          </Card>
        )}

        {/* Execution list */}
        {!isLoading && !error && executions.length > 0 && (
          <>
            <div className="mb-4 text-sm text-gray-500">
              共 {total} 条执行记录
            </div>

            {executions.map((execution) => (
              <ExecutionItem
                key={execution.id}
                execution={execution}
                category={category}
              />
            ))}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-6">
                <Button
                  variant="secondary"
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                >
                  上一页
                </Button>
                <span className="text-sm text-gray-500">
                  {page + 1} / {totalPages}
                </span>
                <Button
                  variant="secondary"
                  onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                >
                  下一页
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
